/**
 * Pattern: Signal Bar Quality (Brooks)
 *
 * Brooks's "signal bar" framework: the bar immediately BEFORE entry is
 * the trigger. A strong signal bar has:
 *   1. Body ≥ 50% of bar's range (decisive close, not a doji)
 *   2. Close in top quartile (for LONG) or bottom quartile (for SHORT)
 *   3. Range ≥ 0.5× recent ATR (not a dead bar)
 *
 * Returns score 0–3 — each criterion contributes 1 point.
 *
 * This is one of the cleanest mechanical patterns: pure bar shape, no
 * swing detection, no indicators, no MACD.
 *
 * Pure function. No side effects.
 */

const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

// Simple N-bar ATR — same shape as in entryDiscipline.js.
const simpleATR = (bars, endIdx, period = 14) => {
  if (endIdx < period) return null;
  let sum = 0;
  let count = 0;
  for (let i = endIdx - period + 1; i <= endIdx; i++) {
    const cur = bars[i];
    const prev = bars[i - 1];
    if (!cur || !prev) continue;
    const hi = Number(cur.high);
    const lo = Number(cur.low);
    const pc = Number(prev.close);
    if (![hi, lo, pc].every(Number.isFinite)) continue;
    sum += Math.max(hi - lo, Math.abs(hi - pc), Math.abs(lo - pc));
    count++;
  }
  return count > 0 ? sum / count : null;
};

export const detectSignalBarQuality = ({ bars, entryTs, direction, options = {} } = {}) => {
  const dir = normalizeDirection(direction);
  if (!dir) return null;
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(entryTs)) return null;

  const minBodyRatio = options.minBodyRatio ?? 0.50;
  const minRangeAtrFraction = options.minRangeAtrFraction ?? 0.50;

  const entryIdx = findEntryBarIdx(bars, entryTs);
  // The SIGNAL bar is the bar BEFORE entry (the trigger candle).
  // If entryIdx is 0 or -1, there's no signal bar to analyze.
  if (entryIdx <= 0) return null;
  const signalBar = bars[entryIdx - 1];
  if (!signalBar) return null;

  const hi = Number(signalBar.high);
  const lo = Number(signalBar.low);
  const op = Number(signalBar.open);
  const cl = Number(signalBar.close);
  if (![hi, lo, op, cl].every(Number.isFinite)) return null;
  const range = hi - lo;
  if (range <= 0) return null;

  // Check 1: body ratio
  const body = Math.abs(cl - op);
  const bodyRatio = body / range;
  const passBody = bodyRatio >= minBodyRatio;

  // Check 2: close position
  // For long: close in top 25% of range (close > lo + 0.75*range)
  // For short: close in bottom 25%
  const closePctOfRange = (cl - lo) / range;
  const passClosePosition = dir === 'long'
    ? closePctOfRange >= 0.75
    : closePctOfRange <= 0.25;

  // Check 3: range vs ATR
  const atr = simpleATR(bars, entryIdx - 1);
  let passRange = false;
  if (Number.isFinite(atr) && atr > 0) {
    passRange = range >= atr * minRangeAtrFraction;
  }

  // Direction confirmation: signal bar should be in the right direction.
  // For long: close > open (green bar). For short: close < open (red bar).
  // This is implicit in passClosePosition for most cases but explicit here
  // for clarity.
  const isCorrectColor = dir === 'long' ? cl >= op : cl <= op;

  const checks = [
    { name: 'body ratio', pass: passBody, value: bodyRatio },
    { name: 'close position', pass: passClosePosition, value: closePctOfRange },
    { name: 'range vs ATR', pass: passRange, value: atr ? range / atr : null },
  ];
  const score = checks.filter(c => c.pass).length;

  return {
    type: 'signal-bar-quality',
    score,
    maxScore: 3,
    strong: score === 3 && isCorrectColor,
    details: `body ${(bodyRatio * 100).toFixed(0)}% · close at ${(closePctOfRange * 100).toFixed(0)}% of range · range ${atr ? (range / atr).toFixed(2) + '× ATR' : '?× ATR'}`,
    evidence: {
      bodyRatio, closePctOfRange, range, atr, isCorrectColor, checks,
    },
  };
};
